/* eslint-disable max-lines */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  ColDef,
  Column,
  ExcelExportParams,
  ExcelStyle,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ProcessCellForExportParams,
  RowNode,
  ValueGetterParams,
} from '@ag-grid-community/core';
import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { Observable } from 'rxjs';

import {
  FileManagerDialogComponent,
  FileManagerDialogInput,
} from '@widgets/file-manager-dialog/file-manager-dialog.component';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';

import { AgActionsComponent } from '@shared/ag-components/ag-actions/ag-actions.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { VendorDropdownComponent } from '@shared/components/vendor-dropdown/vendor-dropdown.component';
import { ChangeLogItemStatusConstants } from '@shared/constants/status.constants';
import { NoopValueAccessorDirective } from '@shared/directives/noop-value-accessor.directive';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { VarDirective } from '@shared/directives/var.directive';
import { ChangeLogItemStatus, DocumentType } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { OrganizationStore } from '@shared/store/organization/organization.store';
import { TrialsQuery } from '@shared/store/trials/trials.query';
import { AuxExcelFormats, AuxExcelStyles } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { AgCisLogActionsComponent } from './ag-cis-log-actions/ag-cis-log-actions.component';
import {
  ChangeLogItemDialogComponent,
  ChangeLogItemDialogComponentInput,
} from './change-log-item-dialog/change-log-item-dialog.component';
import { ChangeLogItemStatusComponent } from './cis-log-status.component';
import { ChangeLogQuery } from './state/change-log.query';
import { ChangeLogService } from './state/change-log.service';
import { ChangeOrderService } from '../change-order/state/change-order.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    VarDirective,
    NgStyle,
    NgClass,
    InputComponent,
    FormsModule,
    NoopValueAccessorDirective,
    VendorDropdownComponent,
    ReactiveFormsModule,
    NgSelectComponent,
    NgOptionComponent,
    ExportExcelButtonComponent,
    IconComponent,
    AgGridAngular,
    StickyGridDirective,
    AsyncPipe,
  ],
  selector: 'aux-cis-log',
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  styleUrls: ['./cis-log.component.css'],
  templateUrl: './cis-log.component.html',
})
export class CisLogComponent implements OnInit {
  changeLogQuery = inject(ChangeLogQuery);
  vendorsQuery = inject(OrganizationQuery);

  private authQuery = inject(AuthQuery);
  private changeLogService = inject(ChangeLogService);
  private changeOrderService = inject(ChangeOrderService);
  private destroyRef = inject(DestroyRef);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private organizationService = inject(OrganizationService);
  private overlayService = inject(OverlayService);
  private trialsQuery = inject(TrialsQuery);
  private vendorsStore = inject(OrganizationStore);

  changeOrderNumbers = new Map<string, string>();
  @ViewChildren('cisLogFilters') cisLogFilters!: QueryList<TemplateRef<unknown>>;
  excelOptions: ExcelExportParams = {
    fileName: 'auxilius-change-in-scope.xlsx',
    processCellCallback(params: ProcessCellForExportParams): string {
      if (params.column.getColId() === 'change_log_item_status') {
        switch (params.value) {
          case ChangeLogItemStatus.STATUS_APPROVED:
            return 'Approved';
          case ChangeLogItemStatus.STATUS_DECLINED:
            return 'Declined';
          case ChangeLogItemStatus.STATUS_ON_HOLD:
            return 'On Hold';
          case ChangeLogItemStatus.STATUS_PENDING_APPROVAL:
            return 'Pending Approval';
          case ChangeLogItemStatus.STATUS_PENDING_REVIEW:
            return 'Pending Review';
          default:
            return Utils.zeroHyphen;
        }
      }

      if (params.column.getColId() === 'requester') {
        switch (params.value) {
          case 'REQUESTER_JOINT':
            return 'Joint';
          case 'REQUESTER_SPONSOR':
            return 'Sponsor';
          case 'REQUESTER_VENDOR':
            return 'Vendor Contract';
          default:
            return Utils.zeroHyphen;
        }
      }

      if (
        params.column.getColId() === 'request_date' ||
        params.column.getColId() === 'start_date'
      ) {
        if (params.value) {
          const element = params.value.split('-');
          const year = element.shift();
          const month = element.shift();
          const day = element.shift();

          return `${year}-${month}-${day}`;
        }

        return Utils.zeroHyphen;
      }

      if (params.column.getColId() === 'is_planned') {
        return params.value ? 'Planned' : 'Unplanned';
      }

      return params.value || params.value === 0 ? params.value : Utils.zeroHyphen;
    },
    sheetName: 'Change In Scope',
  };
  gridAPI!: GridApi;
  gridOptions = {
    columnDefs: [
      {
        cellRenderer: AgActionsComponent,
        cellRendererParams: {
          deleteClickFN: ({ rowNode }: { rowNode: RowNode }) => {
            this.onRemoveLine(rowNode);
          },
          editClickFN: ({ rowNode }: { rowNode: RowNode }) => {
            this.onUpdateLine(rowNode);
          },
          hideDeleteButton: !this.authQuery.getValue().is_admin,
        },
        editable: false,
        field: 'actions',
        headerName: '',
        suppressSizeToFit: true,
        width: 80,
      },
      {
        field: 'cnf_no',
        headerClass: 'ag-header-align-center',
        headerName: 'CNF #',
        maxWidth: 120,
        minWidth: 50,
        sort: 'desc',
        sortable: true,
        valueFormatter: Utils.dashFormatter,
        width: 80,
      },
      {
        cellStyle: { 'text-align': 'center' },
        field: 'description',
        headerClass: 'ag-header-align-center',
        headerName: 'Description',
        maxWidth: 300,
        minWidth: 100,
        sortable: true,
        tooltipField: 'description',
        valueFormatter: Utils.dashFormatter,
        width: 150,
      },
      {
        cellRenderer: ChangeLogItemStatusComponent,
        field: 'change_log_item_status',
        filter: true,
        headerClass: 'ag-header-align-center',
        headerName: 'Status',
        maxWidth: 300,
        minWidth: 100,
        sortable: true,
        valueFormatter: Utils.dashFormatter,
        width: 150,
      },
      {
        cellClass: ['ag-cell-align-right', 'cost'],
        columnGroupShow: 'closed',
        field: 'service_amount',
        headerClass: 'ag-header-align-center',
        headerName: 'Services',
        maxWidth: 150,
        minWidth: 70,
        sortable: true,
        valueFormatter: Utils.agCurrencyFormatter,
        width: 110,
      },
      {
        cellClass: ['ag-cell-align-right', 'cost'],
        columnGroupShow: 'closed',
        field: 'passthrough_amount',
        headerClass: 'ag-header-align-center',
        headerName: 'Pass - through ',
        maxWidth: 150,
        minWidth: 70,
        sortable: true,
        valueFormatter: Utils.agCurrencyFormatter,
        width: 110,
      },
      {
        cellClass: ['ag-cell-align-right', 'cost'],
        columnGroupShow: 'closed',
        field: 'investigator_amount',
        headerClass: 'ag-header-align-center',
        headerName: 'Investigator',
        maxWidth: 150,
        minWidth: 70,
        sortable: true,
        valueFormatter: Utils.agCurrencyFormatter,
        width: 110,
      },
      {
        cellClass: ['ag-cell-align-right', 'cost'],
        colId: 'total_amount',
        columnGroupShow: 'open',
        headerClass: 'ag-header-align-center',
        headerName: 'Total',
        maxWidth: 150,
        minWidth: 70,
        sortable: true,
        valueFormatter: Utils.agCurrencyFormatter,
        valueGetter: this.totalValueGetter,
        width: 110,
      },
      {
        field: 'request_date',
        headerClass: 'ag-header-align-center',
        headerName: 'Request Date',
        maxWidth: 240,
        minWidth: 80,
        sortable: true,
        valueFormatter: (x) => {
          if (x.value) {
            return Utils.agDateFormatter(x);
          }

          return '—';
        },
        width: 120,
      },
      {
        cellClass: 'text-left',
        field: 'requester',
        headerClass: 'ag-header-align-center',
        headerName: 'Requester',
        maxWidth: 150,
        minWidth: 70,
        sortable: true,
        valueFormatter: (v) => {
          const rVal = Utils.dashFormatter(v);

          if (rVal !== Utils.zeroHyphen) {
            return Utils.toProperCase(rVal.replace('REQUESTER_', ''));
          }

          return rVal;
        },
        width: 110,
      },
      {
        cellClass: 'text-left',
        cellDataType: String,
        field: 'is_planned',
        headerClass: 'ag-header-align-center',
        headerName: 'Type',
        maxWidth: 150,
        minWidth: 90,
        sortable: true,
        valueFormatter: (v) => (v.value ? 'Planned' : 'Unplanned'),
        width: 120,
      },
      {
        cellClass: 'text-left',
        field: 'cause',
        headerClass: 'ag-header-align-center',
        headerName: 'Cause',
        maxWidth: 250,
        minWidth: 100,
        sortable: true,
        tooltipField: 'cause',
        valueFormatter: Utils.dashFormatter,
        width: 150,
      },
      {
        cellRenderer: AgCisLogActionsComponent,
        field: 'file_link',
        getQuickFilterText: () => '',
        headerClass: 'ag-header-align-center',
        headerName: 'Attachments',
        maxWidth: 160,
        minWidth: 80,
        sortable: true,
        width: 120,
      },
      {
        field: 'start_date',
        headerClass: 'ag-header-align-center',
        headerName: 'Approved Date',
        maxWidth: 240,
        minWidth: 80,
        sortable: true,
        valueFormatter: (x) => {
          if (x.value) {
            return Utils.agDateFormatter(x);
          }

          return '—';
        },
        width: 120,
      },
      {
        colId: 'change_order_reference',
        headerClass: 'ag-header-align-center',
        headerName: 'Change Order #',
        maxWidth: 300,
        minWidth: 70,
        sortable: true,
        valueFormatter: Utils.dashFormatter,
        valueGetter: (params: ValueGetterParams) =>
          this.changeOrderNumbers.get(params.data.change_order_reference),
        width: 110,
      },
    ],
    defaultColDef: {
      autoHeight: true,
      resizable: true,
      sortable: false,
      suppressMenu: true,
      suppressMovable: true,
      suppressSizeToFit: true,
    },
    suppressCellFocus: true,
  } as GridOptions;
  nameFilterValue = '';
  selectedStatus = new UntypedFormControl('');
  selectedVendor = new UntypedFormControl('');
  showAnalyticsSection$: Observable<boolean>;
  statusConstant = ChangeLogItemStatusConstants.config;

  ngOnInit(): void {
    this.changeLogService.get().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.vendorsQuery.allVendors$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((vendors) => {
      this.selectedVendor.setValue(vendors[0]?.id || '');
      this.vendorsStore.setActive(vendors[0]?.id || null);
    });
    this.changeLogQuery
      .selectActive((x) => x.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((id) => {
        if (id && !Array.isArray(id)) {
          this.changeLogService.getLogItems(id as string).subscribe();
        }

        this.gridOptions = {
          ...this.gridOptions,
          excelStyles: this.generateExcelStyle(this.gridOptions.columnDefs || []),
        };
      });
    this.selectedStatus.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.statusFilter();
    });
    this.changeOrderService
      .listChangeOrders()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((changeOrder) => {
        changeOrder.data?.forEach((changeOrderItem) => {
          this.changeOrderNumbers.set(changeOrderItem.id, changeOrderItem.change_order_no);
        });
      });
  }

  autoSize() {
    this.gridAPI.sizeColumnsToFit();
  }

  constructor() {
    this.showAnalyticsSection$ = this.launchDarklyService.select$(
      (flags) => flags.section_change_analytics
    );

    this.organizationService.get().pipe(takeUntilDestroyed()).subscribe();
  }

  generateExcelStyle(columnDefs: ColDef[]): ExcelStyle[] {
    const styles = columnDefs.map((cd) => {
      let dataType = 'string';
      let format;

      if (cd.field?.endsWith('::costs') || cd.field?.endsWith('::#')) {
        dataType = 'Number';
        format = AuxExcelFormats.Cost;
      }

      return { dataType, id: cd.field, numberFormat: { format } } as ExcelStyle;
    });

    return [...AuxExcelStyles, ...styles];
  }

  getIgnoreCols(colId?: string): boolean {
    if (colId?.endsWith('file_link')) {
      return false;
    }

    return !colId?.endsWith('actions');
  }

  onAddLine() {
    const change_log_id = this.changeLogQuery.getActiveId() as string;
    const cnfNums: Record<string, number> = {};

    this.gridAPI?.forEachNode((row) => {
      cnfNums[row.data.id] = row.data.cnf_no;
    });

    if (change_log_id) {
      this.overlayService.openPopup<
        { change_log_id: string; cnfNums?: Record<string, number> },
        unknown,
        ChangeLogItemDialogComponent
      >({
        data: { change_log_id, cnfNums: cnfNums },
        modal: ChangeLogItemDialogComponent,
        settings: {
          header: 'Add CNF Log Item',
          primaryButton: {
            label: 'Create Item',
          },
        },
      });
    }
  }

  onGridReady(e: GridReadyEvent) {
    this.gridAPI = e.api;
    const allColumnIds: string[] = [];

    this.gridAPI.getColumns()?.forEach((column: Column) => {
      allColumnIds.push(column.getColId());
    });
    this.gridAPI.autoSizeColumns(allColumnIds, false);
  }

  onRemoveLine(rowNode: RowNode) {
    if (!rowNode.data) {
      return;
    }

    const change_log_id = this.changeLogQuery.getActiveId() as string;

    if (change_log_id) {
      const { cnf_no, id } = rowNode.data;

      this.overlayService.openPopup<
        { message: string },
        boolean | undefined,
        ConfirmationModalComponent
      >({
        content: ConfirmationModalComponent,
        data: {
          message: `Are you sure you want to remove Change Log Item ${cnf_no}?`,
        },
        settings: {
          header: 'Remove Change Log Item',
          primaryButton: {
            action: (instance) => {
              instance?.ref.close();
              void this.changeLogService.deleteLogItem(id, change_log_id);
            },
            label: 'Remove',
          },
        },
      });
    }
  }

  onUpdateLine(rowNode: RowNode) {
    const { id: sponsor_organization_id } =
      this.trialsQuery.getEntity(this.mainQuery.getValue().trialKey)?.sponsor_organization || {};

    const change_log_id = this.changeLogQuery.getActiveId() as string;

    const cnfNums: Record<string, number> = {};

    this.gridAPI?.forEachNode((row) => {
      if (row.data.id !== rowNode.data.id) {
        cnfNums[row.data.id] = row.data.cnf_no;
      }
    });

    if (change_log_id) {
      const {
        cause,
        change_log_item_status,
        change_order_reference,
        cnf_no,
        description,
        id,
        investigator_amount,
        is_planned,
        org_id,
        passthrough_amount,
        request_date,
        requester,
        service_amount,
        start_date,
      } = rowNode.data;

      this.overlayService.openPopup<
        ChangeLogItemDialogComponentInput,
        unknown,
        ChangeLogItemDialogComponent
      >({
        data: {
          change_log_id,
          change_log_item_id: id,
          cnfNums: cnfNums,
          formValues: {
            cause,
            change_log_item_status,
            change_order_reference,
            cnf_no,
            description,
            investigator_fee: investigator_amount,
            is_planned,
            is_sponsor: org_id === sponsor_organization_id,
            passthrough_fee: passthrough_amount,
            request_date,
            requester,
            service_fee: service_amount,
            start_date,
          },
          mode: 'update',
        },
        modal: ChangeLogItemDialogComponent,
        settings: {
          header: 'Update CNF Log Item',
          primaryButton: {
            action: (instance) => instance?.onUpdateClick(),
            label: 'Update Item',
          },
        },
      });
    }
  }

  onVendorSelected(vendorId: string) {
    this.vendorsStore.setActive(vendorId);
    this.selectedStatus.setValue('');
  }

  openUploadModal() {
    const trial_id = this.mainQuery.getValue().trialKey;
    const { log_no } = this.changeLogQuery.getActive() || {};
    const vendor_id = this.selectedVendor.value;

    this.overlayService.openPopup<FileManagerDialogInput, unknown, FileManagerDialogComponent>({
      content: FileManagerDialogComponent,
      data: {
        metadata: {
          documentType: DocumentType.DOCUMENT_CNF_LOG_ITEM,
          onboarding: 'false',
          vendor: vendor_id,
        },
        path: `trials/${trial_id}/vendors/${vendor_id}/changelogs/${log_no}/`,
      },
      settings: {
        header: 'Upload New CIS Log',
        hideActions: true,
      },
    });
  }

  statusFilter() {
    this.gridAPI.setFilterModel(null);
    const statusFilter = {
      change_log_item_status: {
        type: 'set',
        values: [this.selectedStatus.value],
      },
    };

    if (this.selectedStatus.value) {
      this.gridAPI.setFilterModel(statusFilter);
    }
  }

  totalValueGetter(params: ValueGetterParams) {
    return (
      params.data.passthrough_amount + params.data.investigator_amount + params.data.service_amount
    );
  }
}
